/**
 * Pantalla de **descuadres** (órdenes con atendido ≠ dueño del rango). Sirve
 * `ROUTES.talonarioMismatches` (`/talonarios/descuadres`). Equivale a
 * `talonario_mismatches.html` del SSR.
 *
 * Exige `manage_talonarios` (vía `RequireManageTalonarios`). Orquesta los filtros
 * de fecha (desde/hasta) y trabajador actual como estado local + el hook de datos
 * `useTalonarioMismatches` (TanStack Query). Replica las columnas del SSR
 * (`progress/explore_talonarios_ssr.md`, §2.5): orden, número de talonario,
 * cliente, atendido actual, esperado por rango, estado y creada. Solo reporte (sin
 * acciones de corrección). Presentacional + hooks; sin `fetch`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";
import { ROUTES } from "@/features/navigation";

import { formatDateTime, talonarioErrorMessage } from "../format";
import { useTalonarioMismatches } from "../hooks/useTalonarioMismatches";
import type { TalonarioMismatch } from "../types";
import { RequireManageTalonarios } from "./RequireManageTalonarios";

/** Estilo del `<select>` coherente con `components/ui/Input`. */
const SELECT_CLASS =
  "block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand";

export function TalonarioMismatchesScreen() {
  // Filtros aplicados (van a la query) vs. los que se editan en el formulario.
  const [applied, setApplied] = useState({ start_date: "", end_date: "", worker: "" });
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [workerInput, setWorkerInput] = useState("");

  const mismatches = useTalonarioMismatches(applied);
  const workers = mismatches.data?.workers ?? [];

  function applyFilters(event: React.FormEvent) {
    event.preventDefault();
    setApplied({
      start_date: startInput.trim(),
      end_date: endInput.trim(),
      worker: workerInput.trim(),
    });
  }

  function clearFilters() {
    setStartInput("");
    setEndInput("");
    setWorkerInput("");
    setApplied({ start_date: "", end_date: "", worker: "" });
  }

  const hasFilters =
    applied.start_date !== "" || applied.end_date !== "" || applied.worker !== "";

  return (
    <RequireManageTalonarios title="Descuadres de talonarios">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            Descuadres de talonarios
          </h1>
          <Button asChild variant="ghost" size="md">
            <Link to={ROUTES.talonarios}>Volver a talonarios</Link>
          </Button>
        </div>

        <form className="flex flex-wrap items-end gap-2" onSubmit={applyFilters}>
          <div className="flex flex-col gap-1">
            <label htmlFor="mismatch-start" className="text-sm font-medium text-slate-700">
              Desde
            </label>
            <Input
              id="mismatch-start"
              type="date"
              value={startInput}
              onChange={(event) => setStartInput(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="mismatch-end" className="text-sm font-medium text-slate-700">
              Hasta
            </label>
            <Input
              id="mismatch-end"
              type="date"
              value={endInput}
              onChange={(event) => setEndInput(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="mismatch-worker" className="text-sm font-medium text-slate-700">
              Atendido
            </label>
            <select
              id="mismatch-worker"
              className={SELECT_CLASS}
              value={workerInput}
              onChange={(event) => setWorkerInput(event.target.value)}
            >
              <option value="">Todos</option>
              {workers.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" size="md">
            Filtrar
          </Button>
          {hasFilters && (
            <Button type="button" variant="ghost" size="md" onClick={clearFilters}>
              Limpiar
            </Button>
          )}
        </form>

        <Card>
          <CardContent className="p-0">
            {mismatches.isPending ? (
              <p className="px-1 py-8 text-center text-sm text-slate-500" role="status">
                Cargando descuadres…
              </p>
            ) : mismatches.isError ? (
              <p className="px-1 py-8 text-center text-sm text-red-600" role="alert">
                {talonarioErrorMessage(mismatches.error)}
              </p>
            ) : (
              <MismatchesTable items={mismatches.data.items} />
            )}
          </CardContent>
        </Card>
      </div>
    </RequireManageTalonarios>
  );
}

/** Tabla de descuadres (presentacional). */
function MismatchesTable({ items }: { items: TalonarioMismatch[] }) {
  if (items.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No se encontraron inconsistencias.
      </p>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Orden</TableHeaderCell>
          <TableHeaderCell>Talonario</TableHeaderCell>
          <TableHeaderCell>Cliente</TableHeaderCell>
          <TableHeaderCell>Atendido actual</TableHeaderCell>
          <TableHeaderCell>Esperado por rango</TableHeaderCell>
          <TableHeaderCell>Estado</TableHeaderCell>
          <TableHeaderCell>Creada</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((mismatch) => (
          <TableRow key={mismatch.order_id}>
            <TableCell className="font-medium text-slate-900">
              {mismatch.order_number}
            </TableCell>
            <TableCell>{mismatch.talonario_number}</TableCell>
            <TableCell>{mismatch.customer_name}</TableCell>
            <TableCell>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                {mismatch.attendant}
              </span>
            </TableCell>
            <TableCell>
              <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                {mismatch.expected_worker}
              </span>
            </TableCell>
            <TableCell className="capitalize">{mismatch.status}</TableCell>
            <TableCell>{formatDateTime(mismatch.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
